import {
  Avatar,
  Box,
  Button,
  Flex,
  HStack,
  Spinner,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Employee } from '../../utils/types';
import { getEmployeeDataMe, getEmployees } from '../../hooks/employees';
import { startChat } from '../../hooks/teamchat';
import {
  TeamChatSearchInput,
  TeamChatSidebarEmpty,
  TeamChatSidebarHeader,
  TeamChatShell,
  teamChatAccent,
} from './components/teamChatUi';

export default function SelectTeamMemberToChatPage() {
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId: string }>();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [meEmployee, setMeEmployee] = useState<Employee>();
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [startingChatId, setStartingChatId] = useState<string | null>(null);
  const toast = useToast();

  useEffect(() => {
    const load = async () => {
      if (!orgId) {
        return;
      }

      setLoading(true);
      try {
        const [meResponse, employeesResponse] = await Promise.all([
          getEmployeeDataMe(orgId),
          getEmployees(orgId),
        ]);

        if (meResponse.success) {
          setMeEmployee(meResponse.data);
        } else {
          toast({ title: 'Failed to load your profile', status: 'error' });
        }

        if (employeesResponse?.data) {
          setEmployees(Array.isArray(employeesResponse.data) ? employeesResponse.data : []);
        }
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [orgId, toast]);

  const filteredEmployees = useMemo(
    () =>
      employees
        .filter((employee) => employee?.id !== meEmployee?.id)
        .filter((employee) =>
          `${employee?.user?.first_name} ${employee?.user?.last_name} ${employee?.role || ''}`
            .toLowerCase()
            .includes(searchTerm.toLowerCase()),
        ),
    [employees, meEmployee?.id, searchTerm],
  );

  const handleSelect = async (employeeId: string) => {
    if (!orgId) {
      return;
    }

    setStartingChatId(employeeId);
    try {
      const response = await startChat(orgId, employeeId);

      if (response.success) {
        navigate(`/organization/${orgId}/teamchat/conversations/${response.data.id}`);
      } else {
        toast({
          title: 'Failed to start chat',
          description: response.data?.detail || 'Please try again.',
          status: 'error',
        });
      }
    } catch {
      toast({
        title: 'Unexpected error',
        description: 'Could not start chat. Try again later.',
        status: 'error',
      });
    } finally {
      setStartingChatId(null);
    }
  };

  return (
    <TeamChatShell
      sidebar={
        <Box w={{ base: '100%', md: '420px' }} bg="white" borderRightWidth={{ base: '0', md: '1px' }} borderColor="gray.200" display="flex" flexDirection="column">
          <TeamChatSidebarHeader
            title="New chat"
            subtitle="Choose a teammate to start a direct conversation"
            action={
              <Button variant="ghost" onClick={() => navigate(`/organization/${orgId}/teamchat/conversations/`)}>
                Back
              </Button>
            }
          />

          <Box px={4} py={3} borderBottomWidth="1px" borderColor="gray.200" bg="#F6F8F9">
            <TeamChatSearchInput
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search teammates"
            />
          </Box>

          <VStack align="stretch" spacing={0} overflowY="auto" flex={1}>
            {loading ? (
              <Flex justify="center" align="center" minH="240px">
                <Spinner color={teamChatAccent} />
              </Flex>
            ) : filteredEmployees.length === 0 ? (
              <TeamChatSidebarEmpty
                title="No teammates found"
                description="Try a different name or role."
              />
            ) : (
              filteredEmployees.map((employee) => (
                <HStack
                  key={employee.id}
                  justify="space-between"
                  px={4}
                  py={3}
                  borderBottomWidth="1px"
                  borderColor="gray.100"
                  _hover={{ bg: '#F7FAFC' }}
                  transition="background 0.16s ease"
                >
                  <HStack spacing={3} minW={0}>
                    <Avatar
                      name={`${employee.user.first_name} ${employee.user.last_name}`}
                      bg={teamChatAccent}
                      color="white"
                      size="md"
                    />
                    <Box minW={0}>
                      <Text fontWeight="700" color="gray.900" noOfLines={1}>
                        {employee.user.first_name} {employee.user.last_name}
                      </Text>
                      <Text fontSize="sm" color="gray.500" noOfLines={1}>
                        {employee.role || 'Team member'}
                      </Text>
                    </Box>
                  </HStack>
                  <Button
                    size="sm"
                    bg={teamChatAccent}
                    color="white"
                    _hover={{ bg: '#17664D' }}
                    isLoading={startingChatId === employee.id}
                    onClick={() => handleSelect(employee.id)}
                    borderRadius="12px"
                  >
                    Chat
                  </Button>
                </HStack>
              ))
            )}
          </VStack>
        </Box>
      }
      thread={
        <Flex
          flex={1}
          display={{ base: 'none', md: 'flex' }}
          align="center"
          justify="center"
          bg="#EFEAE2"
          px={8}
        >
          <VStack spacing={3} maxW="360px" textAlign="center">
            <Avatar size="2xl" bg={teamChatAccent} color="white" name="New chat" />
            <Text fontWeight="800" fontSize="xl" color="gray.900">
              Start a direct conversation
            </Text>
            <Text fontSize="sm" color="gray.500">
              Pick a teammate from the left and the conversation will open in the full chat workspace.
            </Text>
          </VStack>
        </Flex>
      }
    />
  );
}
